import type { PageInfo } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/hooks/useProductRows';
import { getLocalStorage } from '@shared/core';
import { useMemo } from 'react';

const THINGS_TO_DO_PAGE_SLUG = 'things-to-do';
const THINGS_TO_DO_EDITOR_SEEN = 'THINGS_TO_DO_EDITOR_SEEN';

const EXPERIMENT_V1_START_DATE = new Date('2025-08-05T00:00:00Z');
const EXPERIMENT_V2_START_DATE = new Date('2025-09-15T00:00:00Z');

/**
 * Whether an event participates in the Things To Do layout experiment V2.
 * Includes post-V2 events and pre-V1 events; excludes the V1 cohort in between.
 *
 * Reads `PageContainer.createdAt`, which is persisted — unlike the synthesized
 * `EventPage.createdAt`, so this cutoff is real. A missing date also stands in
 * for "event has no Things To Do container".
 */
export const shouldParticipateInExperiment = (pageCreationDate?: string | null): boolean => {
  if (!pageCreationDate) return false;

  const creationDate = new Date(pageCreationDate);
  return creationDate >= EXPERIMENT_V2_START_DATE || creationDate < EXPERIMENT_V1_START_DATE;
};

//util function to check if a given slug is the things to do page slug
export const isThingsToDoSlug = (slug: string) => {
  return slug === THINGS_TO_DO_PAGE_SLUG;
};

export function useThingsToDoPage(eventPages: ReadonlyArray<PageInfo>) {
  // Indicates whether the event has a system-generated Things To Do page
  const eventHasSystemCreatedThingsToDoPage = useMemo(() => {
    return eventPages.some(item => item.pageSlug === THINGS_TO_DO_PAGE_SLUG);
  }, [eventPages]);

  const localStorage = getLocalStorage();

  const isThingsToDoPageEditorSeen = useMemo(() => {
    return localStorage.getItem(THINGS_TO_DO_EDITOR_SEEN) === 'true';
  }, [localStorage]);

  const markThingsToDoPageEditorSeen = () => {
    if (isThingsToDoPageEditorSeen) return;
    localStorage.setItem(THINGS_TO_DO_EDITOR_SEEN, 'true');
  };

  return { eventHasSystemCreatedThingsToDoPage, markThingsToDoPageEditorSeen, isThingsToDoSlug };
}
